export type RecommendationAnalysisRoom = {
	isAnchor: boolean;
	basePrice: number | string;
	changeRate: number | string;
};

export type RecommendationAnalysisState<
	RoomMappingType extends string,
	Room extends RecommendationAnalysisRoom
> = {
	isValid: boolean;
	roomMappingType: RoomMappingType;
	minimumBasePriceRoomStates: readonly Room[] | null | undefined;
};

export type RecommendationAnalysisSource<State, Room> = {
	state: State;
	room: Room;
};

const finiteNumber = (value: number | string): number | undefined => {
	const number = Number(value);
	return Number.isFinite(number) ? number : undefined;
};

export const findLowestAnchorRoom = <Room extends RecommendationAnalysisRoom>(
	rooms: readonly Room[] | null | undefined
): Room | undefined => {
	let lowestRoom: Room | undefined;
	let lowestPrice: number | undefined;

	for (const room of rooms ?? []) {
		if (!room.isAnchor) continue;

		const price = finiteNumber(room.basePrice);
		if (price === undefined || price <= 0) continue;
		if (lowestPrice === undefined || price < lowestPrice) {
			lowestRoom = room;
			lowestPrice = price;
		}
	}

	return lowestRoom;
};

export const selectRecommendationAnalysisSource = <
	RoomMappingType extends string,
	Room extends RecommendationAnalysisRoom,
	State extends RecommendationAnalysisState<RoomMappingType, Room>
>(
	states: readonly State[],
	roomMappingTypeOrder: readonly RoomMappingType[]
): RecommendationAnalysisSource<State, Room> | undefined => {
	let source: RecommendationAnalysisSource<State, Room> | undefined;

	for (const state of states) {
		if (!state.isValid) continue;

		const room = (state.minimumBasePriceRoomStates ?? []).reduce<Room | undefined>(
			(lowestRoom, candidate) => {
				if (!candidate.isAnchor) return lowestRoom;

				const candidatePrice = finiteNumber(candidate.basePrice);
				if (candidatePrice === undefined || candidatePrice <= 0) return lowestRoom;
				if (!lowestRoom || candidatePrice < Number(lowestRoom.basePrice)) return candidate;
				return lowestRoom;
			},
			undefined
		);
		if (!room) continue;

		const basePrice = finiteNumber(room.basePrice);
		const changeRate = finiteNumber(room.changeRate);
		if (basePrice === undefined || basePrice <= 0 || changeRate === undefined || changeRate === 0)
			continue;

		if (!source) {
			source = { state, room };
			continue;
		}

		const sourceBasePrice = Number(source.room.basePrice);
		if (basePrice < sourceBasePrice) {
			source = { state, room };
			continue;
		}
		if (basePrice > sourceBasePrice) continue;

		const stateIndex = roomMappingTypeOrder.indexOf(state.roomMappingType);
		const sourceIndex = roomMappingTypeOrder.indexOf(source.state.roomMappingType);
		const stateRank = stateIndex === -1 ? Number.MAX_SAFE_INTEGER : stateIndex;
		const sourceRank = sourceIndex === -1 ? Number.MAX_SAFE_INTEGER : sourceIndex;
		if (stateRank < sourceRank) source = { state, room };
	}

	return source;
};

export type RecommendationDriver<DriverType extends string> = {
	type: DriverType;
	impact: number;
};

export const orderRecommendationDrivers = <DriverType extends string>(
	drivers: readonly RecommendationDriver<DriverType>[] | null | undefined,
	driverOrder: readonly DriverType[]
): RecommendationDriver<DriverType>[] => {
	return driverOrder.map(
		type => drivers?.find(driver => driver.type === type) ?? { type, impact: 0 }
	);
};

export const getDriverImpactLevel = (impact: number | null | undefined): number => {
	const numericImpact = Number(impact ?? 0);
	const normalizedImpact = Number.isFinite(numericImpact) ? numericImpact : 0;
	return Math.min(4, Math.max(0, Math.ceil(normalizedImpact * 4)));
};

export const DRIVER_IMPACT_LEVEL_LABELS = ['None', 'Low', 'Moderate', 'High', 'Very high'] as const;

// Match the table: one lowest anchor and one lowest rate per derivative room.
export const recommendationTableRooms = <Room extends RecommendationAnalysisRoom & { roomId: number; isDerivative: boolean }>(rooms: readonly Room[]): Room[] => {
	const anchor = findLowestAnchorRoom(rooms);
	if (anchor === undefined) return [];
	const derivatives = new Map<number, Room>();
	for (const room of rooms) {
		if (!room.isDerivative) continue;
		const current = derivatives.get(room.roomId);
		if (current === undefined || Number(room.basePrice) < Number(current.basePrice)) derivatives.set(room.roomId, room);
	}
	return [anchor, ...Array.from(derivatives.values()).sort((a, b) => Number(a.basePrice) - Number(b.basePrice))];
};

// Match the room table's whole-percent rounding, including negative ties.
export const roomPriceChangePercent = (basePrice: number, finalPrice: number): number => {
	const rounded = Math.round(((finalPrice - basePrice) * 100) / basePrice);
	return rounded === 0 || Number.isNaN(rounded) ? 0 : rounded;
};
